import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, LockKey, Plus, ShieldCheck, Trash, Warning } from 'phosphor-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { IGroup, useGroups } from '../../hooks/useGroups';
import { IIpWhitelist } from '../../hooks/useIpWhitelist';
import { useDialog } from '../../providers/dialog';
import { IData } from '../../types/shared';
import { acessoApi } from './acessoApi';
import {
  AZUL,
  AZUL_ESCURO,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  Interruptor,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

const GRUPO_ADMIN = 1;
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}$/;
const ipValido = (ip: string) => IPV4.test(ip) && ip.split('.').every(p => Number(p) <= 255);

/** Grupos que, hoje, só entram de IPs liberados (regra do login: IP ativo, tipo login). */
function restricoesPorGrupo(ips: IIpWhitelist[]) {
  const mapa = new Map<number, IIpWhitelist[]>();
  ips
    .filter(i => i.ativo && i.tipo === 'login')
    .forEach(i => i.grupo_ids.forEach(g => mapa.set(g, [...(mapa.get(g) ?? []), i])));
  return mapa;
}

function useGruposDoSistema() {
  const { getGroups } = useGroups();
  const { data } = useQuery<IData<IGroup[]>>({
    queryKey: ['groups', { size: 100 }],
    queryFn: getGroups,
  });
  return (data?.data?.items ?? []).filter(g => g.id && g.id !== GRUPO_ADMIN);
}

function MapaPorGrupo({ ips, onAbrir }: { ips: IIpWhitelist[]; onAbrir: (id: number) => void }) {
  const grupos = useGruposDoSistema();
  const mapa = restricoesPorGrupo(ips);
  const restritos = grupos.filter(g => mapa.has(g.id as number));
  // grupos com exatamente os mesmos IPs viram um bloco só
  const blocos: { chave: string; grupos: IGroup[]; ips: IIpWhitelist[] }[] = [];
  restritos.forEach(g => {
    const porIp = new Map<string, IIpWhitelist>();
    (mapa.get(g.id as number) ?? []).forEach(i => {
      if (!porIp.has(i.ip)) porIp.set(i.ip, i);
    });
    const lista = Array.from(porIp.values()).sort((a, b) => a.ip.localeCompare(b.ip));
    const chave = lista.map(i => i.ip).join('|');
    const bloco = blocos.find(b => b.chave === chave);
    if (bloco) bloco.grupos.push(g);
    else blocos.push({ chave, grupos: [g], ips: lista });
  });
  const livres = grupos.filter(g => !mapa.has(g.id as number));

  return (
    <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
      <Box>
        <Eyebrow>DE ONDE CADA GRUPO PODE ENTRAR</Eyebrow>
        <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px">
          {restritos.length === 0
            ? 'Nenhum grupo está preso a um IP'
            : `${restritos.length} ${restritos.length === 1 ? 'grupo só entra' : 'grupos só entram'} de IPs liberados`}
        </Text>
        <Text fontSize="13px" color="#475569">
          Um grupo marcado em algum IP de login passa a entrar <strong>somente</strong> desses IPs.
          Grupo sem IP entra de qualquer lugar. Administradores nunca são barrados.
        </Text>
      </Box>

      {blocos.map(b => (
        <Flex
          key={b.chave}
          direction="column"
          gap="10px"
          p="14px"
          borderRadius="16px"
          bg="rgba(254,243,199,.45)"
          boxShadow="inset 0 0 0 1px #FDE68A"
        >
          <Flex align="center" gap="10px" wrap="wrap">
            <LockKey size={18} color="#92400E" />
            <Text fontSize="14px" fontWeight={700} color={TEXTO}>
              {b.grupos.map(g => g.nome).join(', ')}
            </Text>
            <Text fontSize="12.5px" color="#92400E">
              só {b.grupos.length === 1 ? 'entra' : 'entram'} de {b.ips.length}{' '}
              {b.ips.length === 1 ? 'IP' : 'IPs'}
            </Text>
          </Flex>
          <Flex gap="6px" wrap="wrap">
            {b.ips.map(ip => (
              <Box
                key={ip.ip}
                as="button"
                type="button"
                onClick={() => onAbrir(ip.id)}
                fontFamily={MONO}
                fontSize="12px"
                px="10px"
                py="3px"
                borderRadius="999px"
                bg="white"
                color="#334155"
                boxShadow="inset 0 0 0 1px rgba(15,23,42,.1)"
                _hover={{ color: AZUL }}
              >
                {ip.ip}
                {ip.descricao ? ` · ${ip.descricao}` : ''}
              </Box>
            ))}
          </Flex>
        </Flex>
      ))}

      {livres.length > 0 && (
        <Flex
          align="flex-start"
          gap="12px"
          p="12px 14px"
          borderRadius="16px"
          bg="rgba(248,250,252,.8)"
          wrap="wrap"
        >
          <Globe size={18} color={TEXTO_SUAVE} />
          <Box flex={1} minW={0}>
            <Text fontSize="13px" fontWeight={700} color="#334155" mb="6px">
              Entram de qualquer lugar
            </Text>
            <Flex gap="6px" wrap="wrap">
              {livres.map(g => (
                <Etiqueta key={g.id} tom="neutro">
                  {g.nome}
                </Etiqueta>
              ))}
            </Flex>
          </Box>
        </Flex>
      )}
    </Cartao>
  );
}

function EditorDeIp({
  ip,
  todos,
  onFechar,
  onSalvo,
}: {
  ip: IIpWhitelist | null;
  todos: IIpWhitelist[];
  onFechar: () => void;
  onSalvo: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const showDialog = useDialog();
  const grupos = useGruposDoSistema();
  const [endereco, setEndereco] = useState(ip?.ip ?? '');
  const [tipo, setTipo] = useState<'login' | 'captcha'>(
    (ip?.tipo as 'login' | 'captcha') ?? 'login',
  );
  const [gruposIds, setGruposIds] = useState<number[]>(ip?.grupo_ids ?? []);
  const [descricao, setDescricao] = useState(ip?.descricao ?? '');
  const [ativo, setAtivo] = useState(ip?.ativo ?? true);

  const enderecoLimpo = endereco.trim();
  const mesmoIp = todos.filter(o => o.id !== ip?.id && o.ip === enderecoLimpo && o.tipo === tipo);
  const valido = ipValido(enderecoLimpo);
  const nomesDe = (lista: IIpWhitelist[]) =>
    lista
      .flatMap(o => o.grupo_ids)
      .map(g => grupos.find(x => x.id === g)?.nome)
      .filter(Boolean)
      .join(', ');

  // quem passa a ficar preso a IP por causa desta mudança
  const outros = restricoesPorGrupo(todos.filter(o => o.id !== ip?.id));
  const novosRestritos =
    tipo === 'login' && ativo
      ? grupos.filter(g => gruposIds.includes(g.id as number) && !outros.has(g.id as number))
      : [];
  const antes = ip && ip.tipo === 'login' && ip.ativo ? ip.grupo_ids : [];
  const depois = tipo === 'login' && ativo ? gruposIds : [];
  const liberados = grupos.filter(
    g =>
      antes.includes(g.id as number) &&
      !depois.includes(g.id as number) &&
      !outros.has(g.id as number),
  );

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['central', 'ips'] });
  const salvar = useMutation({
    mutationFn: () =>
      acessoApi.salvarIp({
        id: ip?.id,
        ip: enderecoLimpo,
        tipo,
        grupo_ids: tipo === 'login' ? gruposIds : [],
        descricao: descricao.trim() || null,
        ativo,
      }),
    onSuccess: async salvo => {
      await invalidar();
      toast.success(ip ? 'IP atualizado' : 'IP adicionado');
      onSalvo(salvo?.id ?? ip?.id ?? 0);
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.[0]?.msg ?? 'Não foi possível salvar o IP'));
    },
  });
  const remover = useMutation({
    mutationFn: () => acessoApi.removerIp(ip?.id as number),
    onSuccess: async () => {
      await invalidar();
      toast.success('IP removido');
      onFechar();
    },
    onError: () => toast.error('Não foi possível remover o IP'),
  });

  const OpcaoDeTipo = ({
    valor,
    titulo,
    texto,
    icone,
  }: {
    valor: 'login' | 'captcha';
    titulo: string;
    texto: string;
    icone: React.ReactNode;
  }) => {
    const marcada = tipo === valor;
    return (
      <Flex
        as="button"
        type="button"
        onClick={() => setTipo(valor)}
        aria-pressed={marcada}
        flex="1 1 220px"
        gap="12px"
        p="14px"
        borderRadius="16px"
        textAlign="left"
        bg={marcada ? '#DFF3FF' : 'white'}
        boxShadow={marcada ? `inset 0 0 0 2px ${AZUL}` : 'inset 0 0 0 1px rgba(15,23,42,.08)'}
      >
        <Box color={marcada ? AZUL : TEXTO_SUAVE} pt="2px">
          {icone}
        </Box>
        <Box>
          <Text fontSize="14px" fontWeight={700} color={TEXTO}>
            {titulo}
          </Text>
          <Text fontSize="12.5px" color="#475569">
            {texto}
          </Text>
        </Box>
      </Flex>
    );
  };

  return (
    <Cartao p="20px 22px" display="flex" flexDirection="column" gap="18px">
      <Flex align="flex-start" justify="space-between" gap="12px" wrap="wrap">
        <Box>
          <Eyebrow>{ip ? 'IP LIBERADO' : 'NOVO IP'}</Eyebrow>
          <Text fontSize="22px" lineHeight="28px" fontWeight={700} fontFamily={MONO}>
            {enderecoLimpo || '0.0.0.0'}
          </Text>
        </Box>
        <Flex align="center" gap="10px">
          <Text fontSize="13px" color="#475569">
            {ativo ? 'Ativo' : 'Inativo'}
          </Text>
          <Interruptor ligado={ativo} onChange={() => setAtivo(a => !a)} rotulo="IP ativo" />
        </Flex>
      </Flex>

      <Flex gap="12px" wrap="wrap">
        <Box flex="1 1 200px">
          <Eyebrow>ENDEREÇO IP</Eyebrow>
          <Input
            mt="6px"
            h="40px"
            borderRadius="10px"
            bg="white"
            fontFamily={MONO}
            placeholder="177.135.249.230"
            value={endereco}
            onChange={e => setEndereco(e.target.value)}
            isInvalid={!!enderecoLimpo && !valido}
          />
          {!!enderecoLimpo && !ipValido(enderecoLimpo) && (
            <Text fontSize="12px" color="#991B1B" mt="4px">
              IP inválido (ex.: 192.168.0.1)
            </Text>
          )}
          {mesmoIp.length > 0 && (
            <Text fontSize="12px" color={TEXTO_SUAVE} mt="4px">
              Este IP já tem {mesmoIp.length}{' '}
              {mesmoIp.length === 1 ? 'outro cadastro' : 'outros cadastros'} desse tipo
              {tipo === 'login' && nomesDe(mesmoIp) ? ` (${nomesDe(mesmoIp)})` : ''}.
            </Text>
          )}
        </Box>
        <Box flex="2 1 260px">
          <Eyebrow>DESCRIÇÃO</Eyebrow>
          <Input
            mt="6px"
            h="40px"
            borderRadius="10px"
            bg="white"
            placeholder="Ex.: Escritório 4"
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
          />
        </Box>
      </Flex>

      <Box>
        <Eyebrow>O QUE ESTE IP FAZ</Eyebrow>
        <Flex gap="10px" wrap="wrap" mt="8px">
          <OpcaoDeTipo
            valor="login"
            titulo="Libera login"
            texto="Os grupos marcados passam a entrar só dos IPs liberados para eles."
            icone={<LockKey size={20} />}
          />
          <OpcaoDeTipo
            valor="captcha"
            titulo="Pula o captcha"
            texto="Quem entra deste IP não precisa resolver o captcha. Vale para todos."
            icone={<ShieldCheck size={20} />}
          />
        </Flex>
      </Box>

      {tipo === 'login' && (
        <Box>
          <Flex justify="space-between" align="center">
            <Eyebrow>GRUPOS PRESOS A ESTE IP</Eyebrow>
            <Contador>{gruposIds.length}</Contador>
          </Flex>
          <Flex gap="6px" wrap="wrap" mt="8px">
            {grupos.map(g => (
              <Pilula
                key={g.id}
                ativa={gruposIds.includes(g.id as number)}
                onClick={() =>
                  setGruposIds(ids =>
                    ids.includes(g.id as number)
                      ? ids.filter(i => i !== g.id)
                      : [...ids, g.id as number],
                  )
                }
              >
                {g.nome}
              </Pilula>
            ))}
          </Flex>
          {gruposIds.length === 0 && (
            <Text fontSize="12.5px" color={TEXTO_SUAVE} mt="8px">
              Sem grupo marcado, este IP não muda nada no login.
            </Text>
          )}
        </Box>
      )}

      {(novosRestritos.length > 0 || liberados.length > 0) && (
        <Flex
          gap="10px"
          align="flex-start"
          p="12px 14px"
          borderRadius="14px"
          bg="#FEFCE8"
          boxShadow="inset 0 0 0 1px #FDE68A"
          color="#92400E"
          fontSize="13px"
        >
          <Warning size={18} />
          <Box>
            {novosRestritos.length > 0 && (
              <Text>
                <strong>{novosRestritos.map(g => g.nome).join(', ')}</strong> hoje{' '}
                {novosRestritos.length === 1 ? 'entra' : 'entram'} de qualquer lugar e, ao salvar,{' '}
                {novosRestritos.length === 1 ? 'passa' : 'passam'} a entrar só deste IP. Quem
                estiver fora dele não consegue mais fazer login.
              </Text>
            )}
            {liberados.length > 0 && (
              <Text>
                <strong>{liberados.map(g => g.nome).join(', ')}</strong> deixa de ter restrição de
                IP e passa a entrar de qualquer lugar.
              </Text>
            )}
          </Box>
        </Flex>
      )}

      <Flex
        gap="8px"
        justify="space-between"
        wrap="wrap"
        pt="4px"
        borderTop="1px solid rgba(15,23,42,.06)"
      >
        {ip ? (
          <Button
            size="sm"
            variant="ghost"
            borderRadius="999px"
            color="#991B1B"
            leftIcon={<Trash size={14} />}
            isLoading={remover.isPending}
            onClick={() =>
              showDialog({
                status: 'warning',
                title: 'Remover este IP?',
                description: `${ip.ip} sai da whitelist. Essa ação não pode ser desfeita.`,
                cancelLabel: 'Cancelar',
                async onConfirm() {
                  await remover.mutateAsync();
                },
              })
            }
          >
            Remover
          </Button>
        ) : (
          <Box />
        )}
        <Flex gap="8px">
          <Button size="sm" variant="ghost" borderRadius="999px" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            borderRadius="999px"
            color="white"
            bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
            _hover={{ bg: AZUL_ESCURO }}
            isDisabled={!valido}
            isLoading={salvar.isPending}
            onClick={() => salvar.mutate()}
          >
            {ip ? 'Salvar alterações' : 'Adicionar IP'}
          </Button>
        </Flex>
      </Flex>
    </Cartao>
  );
}

/** Aba "Whitelist de IPs": de onde cada grupo pode entrar e quem pula o captcha. */
export function WhitelistCentral() {
  const [params, setParams] = useSearchParams();
  const selecionado = params.get('ip');
  const [busca, setBusca] = useState('');
  const { data: ips = [], isLoading } = useQuery({
    queryKey: ['central', 'ips'],
    queryFn: acessoApi.ips,
  });
  const grupos = useGruposDoSistema();
  const nomeDoGrupo = useMemo(() => new Map(grupos.map(g => [g.id, g.nome])), [grupos]);

  const abrir = (v: string | null) => {
    const p = new URLSearchParams(params);
    if (v) p.set('ip', v);
    else p.delete('ip');
    setParams(p, { replace: true });
  };

  const t = busca.trim().toLowerCase();
  const filtrados = ips.filter(
    i => !t || i.ip.includes(t) || (i.descricao ?? '').toLowerCase().includes(t),
  );
  // o mesmo IP costuma ter um cadastro por grupo: na lista vira uma linha só
  const agrupar = (lista: IIpWhitelist[]) => {
    const porIp = new Map<string, IIpWhitelist[]>();
    lista.forEach(i => porIp.set(i.ip, [...(porIp.get(i.ip) ?? []), i]));
    return Array.from(porIp.entries()).map(([endereco, cadastros]) => ({ endereco, cadastros }));
  };
  const secoes = [
    {
      titulo: 'LIBERAM LOGIN',
      login: true,
      itens: agrupar(filtrados.filter(i => i.tipo === 'login')),
    },
    {
      titulo: 'PULAM O CAPTCHA',
      login: false,
      itens: agrupar(filtrados.filter(i => i.tipo !== 'login')),
    },
  ];
  const ipAberto = ips.find(i => String(i.id) === selecionado) ?? null;

  return (
    <Flex gap="20px" align="flex-start" wrap="wrap">
      <Cartao
        as="section"
        aria-label="IPs liberados"
        flex="1 1 280px"
        maxW={{ base: '100%', lg: '340px' }}
        minW={0}
        p="18px"
        display="flex"
        flexDirection="column"
        gap="12px"
      >
        <Flex align="center" justify="space-between" gap="8px">
          <Eyebrow>IPS LIBERADOS</Eyebrow>
          <Button
            size="xs"
            borderRadius="999px"
            variant="outline"
            bg="white"
            leftIcon={<Plus size={12} />}
            onClick={() => abrir('novo')}
          >
            Novo IP
          </Button>
        </Flex>
        <Busca valor={busca} onChange={setBusca} placeholder="IP ou descrição" />
        {isLoading && (
          <Flex justify="center" py={6}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {secoes.map(s => (
          <Flex key={s.titulo} direction="column" gap="4px">
            <Flex justify="space-between" align="center" mt="4px">
              <Eyebrow>{s.titulo}</Eyebrow>
              <Contador>{s.itens.length}</Contador>
            </Flex>
            {s.itens.length === 0 && !isLoading && (
              <Text fontSize="12.5px" color={TEXTO_SUAVE} py="4px">
                Nenhum.
              </Text>
            )}
            {s.itens.map(({ endereco, cadastros }) => {
              const aberto = cadastros.some(c => String(c.id) === selecionado);
              const todosInativos = cadastros.every(c => !c.ativo);
              const descricoes = Array.from(
                new Set(cadastros.map(c => c.descricao).filter(Boolean)),
              );
              return (
                <Box
                  key={endereco}
                  p="8px 10px"
                  borderRadius="14px"
                  bg={aberto ? '#DFF3FF' : 'transparent'}
                  boxShadow={aberto ? `inset 0 0 0 1px ${AZUL}33` : undefined}
                  opacity={todosInativos ? 0.55 : 1}
                  _hover={aberto ? undefined : { bg: 'rgba(255,255,255,.8)' }}
                >
                  <Flex
                    as="button"
                    type="button"
                    w="100%"
                    textAlign="left"
                    direction="column"
                    align="stretch"
                    onClick={() => abrir(String(cadastros[0].id))}
                  >
                    <Flex align="center" justify="space-between" gap="8px">
                      <Text fontFamily={MONO} fontSize="13px" fontWeight={600} color={TEXTO}>
                        {endereco}
                      </Text>
                      {todosInativos && <Etiqueta tom="neutro">inativo</Etiqueta>}
                    </Flex>
                    <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                      {descricoes.join(', ') || (s.login ? '' : 'qualquer pessoa')}
                    </Text>
                  </Flex>
                  {s.login && (
                    <Flex gap="4px" wrap="wrap" mt="6px">
                      {cadastros.map(c => {
                        const nomes = c.grupo_ids.map(g => nomeDoGrupo.get(g) ?? `Grupo ${g}`);
                        const esse = String(c.id) === selecionado;
                        let cor = c.ativo ? '#334155' : '#94A3B8';
                        if (esse) cor = 'white';
                        return (
                          <Box
                            key={c.id}
                            as="button"
                            type="button"
                            onClick={() => abrir(String(c.id))}
                            fontSize="11px"
                            fontWeight={600}
                            px="8px"
                            py="1px"
                            borderRadius="999px"
                            bg={esse ? AZUL : 'white'}
                            color={cor}
                            boxShadow={esse ? undefined : 'inset 0 0 0 1px rgba(15,23,42,.1)'}
                            textDecoration={c.ativo ? undefined : 'line-through'}
                          >
                            {nomes.length ? nomes.join(', ') : 'nenhum grupo'}
                          </Box>
                        );
                      })}
                    </Flex>
                  )}
                </Box>
              );
            })}
          </Flex>
        ))}
      </Cartao>

      <Box flex="999 1 520px" minW={0}>
        {selecionado === 'novo' || ipAberto ? (
          <EditorDeIp
            key={selecionado ?? ''}
            ip={ipAberto}
            todos={ips}
            onFechar={() => abrir(null)}
            onSalvo={id => abrir(id ? String(id) : null)}
          />
        ) : (
          <MapaPorGrupo ips={ips} onAbrir={id => abrir(String(id))} />
        )}
      </Box>
    </Flex>
  );
}
